import React, { memo, useCallback, useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    Chip,
    Checkbox,
    useTheme
} from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    ScrollableTableContainer,
    StyledTableCell,
    StyledTableRow
} from '../styles';

const InvoiceTable = memo(({
    invoiceData,
    page,
    rowsPerPage,
    totalCount,
    isLoading,
    columnsToUse,
    checkedColumns,
    selectedItems,
    onSelectItem,
    onSelectAll,
    handleChangePage,
    handleChangeRowsPerPage,
    onInvoicePreviewById,
    t
}) => {
    const { t: translate } = useTranslation();
    const theme = useTheme();
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const visibleColumns = columnsToUse;

    const getSortableValue = useCallback((row, key) => {
        switch (key) {
            case 'invoice_date':
            case 'due_date':
                return row[key] ? dayjs(row[key]).valueOf() : 0;
            case 'amount':
            case 'vat_amount':
            case 'total_amount':
            case 'received_amount':
                return Number(row[key]) || 0;
            case 'invoice_no':
            case 'customer_name':
            case 'manager_name':
            case 'currency':
            case 'status':
            case 'status_account_receivable':
                return row[key] || '';
            default:
                return row[key] ?? '';
        }
    }, []);

    const sortedData = useMemo(() => {
        if (!sortConfig.key || !sortConfig.direction) {
            return invoiceData;
        }

        const sorted = [...invoiceData];
        sorted.sort((a, b) => {
            const aValue = getSortableValue(a, sortConfig.key);
            const bValue = getSortableValue(b, sortConfig.key);

            if (aValue === bValue) {
                return (Number(a.invoice_id) || 0) - (Number(b.invoice_id) || 0);
            }
            if (aValue === null || aValue === undefined) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (bValue === null || bValue === undefined) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            const compared = String(aValue).localeCompare(String(bValue));
            return sortConfig.direction === 'asc' ? compared : -compared;
        });
        return sorted;
    }, [invoiceData, sortConfig, getSortableValue]);

    const handleSort = useCallback((columnKey) => {
        setSortConfig((prev) => {
            if (prev.key === columnKey) {
                if (prev.direction === 'asc') {
                    return { key: columnKey, direction: 'desc' };
                }
                if (prev.direction === 'desc') {
                    return { key: null, direction: null };
                }
            }
            return { key: columnKey, direction: 'asc' };
        });
    }, []);

    const formatPrice = (price) => {
        if (!price) return '-';
        return parseFloat(price).toFixed(2);
    };

    const formatAmount = (amount) => {
        if (!amount) return '-';
        return parseFloat(amount).toFixed(2);
    };

    const getCellAlignment = (columnId) => {
        const centerAlignedColumns = [
            'customer_name', 'manager_name', 'invoice_no', 'invoice_date', 'due_date',
            'currency', 'amount', 'vat_amount', 'total_amount', 'received_amount', 'status', 'status_account_receivable'
        ];

        return centerAlignedColumns.includes(columnId) ? 'center' : 'left';
    };

    const currentPageData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const currentPageIds = currentPageData.map(item => item.invoice_id);
    const selectedCurrentPageItems = selectedItems.filter(id => currentPageIds.includes(id));
    
    const isAllSelected = currentPageData.length > 0 && selectedCurrentPageItems.length === currentPageData.length;
    const isIndeterminate = selectedCurrentPageItems.length > 0 && selectedCurrentPageItems.length < currentPageData.length;

    if (sortedData.length === 0 && !isLoading) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                    {t('invoice.noData')}
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <ScrollableTableContainer component={Paper} sx={{
                maxHeight: 'calc(100vh - 280px)',
                overflow: 'auto',
                borderRadius: 2,
                boxShadow: theme.palette.mode === 'dark' 
                    ? '0 1px 3px rgba(0,0,0,0.3)' 
                    : '0 1px 3px rgba(0,0,0,0.05)',
                border: theme.palette.mode === 'dark' 
                    ? `1px solid ${theme.palette.divider}` 
                    : '1px solid #e5e7eb',
                backgroundColor: theme.palette.mode === 'dark' 
                    ? theme.palette.background.paper 
                    : '#ffffff',
            }}>
                <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                    <TableHead sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? theme.palette.grey[800] 
                            : '#f8fafc',
                        '& .MuiTableCell-head': {
                            borderRight: 'none !important',
                            borderLeft: 'none !important',
                            borderBottom: theme.palette.mode === 'dark' 
                                ? `2px solid ${theme.palette.divider}` 
                                : '2px solid #e5e7eb',
                            fontWeight: 600,
                            color: theme.palette.mode === 'dark' 
                                ? theme.palette.text.primary 
                                : '#374151'
                        }
                    }}>
                        <TableRow>
                            <StyledTableCell padding="checkbox" align="center">
                                <Checkbox
                                    indeterminate={isIndeterminate}
                                    checked={isAllSelected}
                                    onChange={(e) => onSelectAll(e.target.checked, currentPageIds)}
                                    sx={{
                                        color: '#ffffff',
                                        '&.Mui-checked': {
                                            color: '#ffffff',
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: '#ffffff',
                                        }
                                    }}
                                />
                            </StyledTableCell>
                            {visibleColumns.map((column) => (
                                <StyledTableCell
                                    key={column.id}
                                    align={getCellAlignment(column.id)}
                                    style={{ minWidth: column.minWidth }}
                                    onClick={() => handleSort(column.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        {column.label}
                                        {sortConfig.key === column.id && sortConfig.direction === 'asc' && (
                                            <ArrowUpward sx={{ fontSize: '0.9rem' }} />
                                        )}
                                        {sortConfig.key === column.id && sortConfig.direction === 'desc' && (
                                            <ArrowDownward sx={{ fontSize: '0.9rem' }} />
                                        )}
                                    </Box>
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <StyledTableCell colSpan={visibleColumns.length + 1} align="center">
                                    <CircularProgress size={24} />
                                </StyledTableCell>
                            </TableRow>
                        ) : (
                            currentPageData.map((row) => (
                                <StyledTableRow
                                    hover
                                    key={row.invoice_id}
                                    selected={selectedItems.includes(row.invoice_id)}
                                >
                                    <StyledTableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedItems.includes(row.invoice_id)}
                                            onChange={() => onSelectItem(row.invoice_id)}
                                        />
                                    </StyledTableCell>
                                    {visibleColumns.map((column) => (
                                        <StyledTableCell key={column.id} align={getCellAlignment(column.id)}>
                                            {column.id === 'invoice_no' &&
                                            onInvoicePreviewById &&
                                            row.invoice_id != null &&
                                            Number.isFinite(Number(row.invoice_id)) ? (
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onInvoicePreviewById(row.invoice_id);
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        color: 'primary.main',
                                                        textDecoration: 'underline',
                                                        '&:hover': { color: 'primary.dark' }
                                                    }}
                                                >
                                                    {column.format
                                                        ? column.format(row[column.id], row)
                                                        : row[column.id] || '-'}
                                                </Typography>
                                            ) : column.render ? (
                                                (() => {
                                                    const renderResult = column.render(row[column.id]);
                                                    if (renderResult && renderResult.type === 'chip') {
                                                        return (
                                                            <Chip
                                                                label={translate(renderResult.label)}
                                                                color={renderResult.color}
                                                                variant={renderResult.variant}
                                                                size={renderResult.size}
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    minWidth: '80px',
                                                                    '& .MuiChip-label': {
                                                                        fontSize: '0.75rem'
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    }
                                                    return column.format ? column.format(row[column.id], row) : row[column.id];
                                                })()
                                            ) : (
                                                column.format ? column.format(row[column.id], row) : row[column.id]
                                            )}
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </ScrollableTableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('common.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} / ${count !== -1 ? count : t('common.moreThan', { count: to })}`
                }
                sx={{
                    '.MuiTablePagination-toolbar': {
                        paddingLeft: 2,
                        paddingRight: 2,
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? theme.palette.background.paper 
                            : '#f8fafc',
                        borderTop: theme.palette.mode === 'dark' 
                            ? `1px solid ${theme.palette.divider}` 
                            : '1px solid #e5e7eb',
                    }
                }}
            />
        </Box>
    );
});

InvoiceTable.displayName = 'InvoiceTable';

export default InvoiceTable; 